import React, { useState, useEffect, useRef, useCallback } from "react";
import Modal from "./Modal";
import PhoneVerificationModal from "./PhoneVerificationModal";
import Slider from "./Slider";
import Tooltip from "./Tooltip";
import { useUser } from "../contexts/UserContext";
import api from "../utils/api";
import "./AccountModal.css";

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AccountModal: React.FC<AccountModalProps> = ({ isOpen, onClose }) => {
  const { user, fetchUserData } = useUser();

  const [editMode, setEditMode] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState<boolean>(false);

  const [editableFields, setEditableFields] = useState({
    name: "",
    email: "",
    phoneNo: "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [passwordMatch, setPasswordMatch] = useState(false);
  const [passwordLength, setPasswordLength] = useState(false);
  const [hasCopiedUserId, setHasCopiedUserId] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Phone verification state
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [pendingPhoneUpdate, setPendingPhoneUpdate] = useState<string | null>(null);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setEditableFields({
        name: user.name || "",
        email: user.email || "",
        phoneNo: formatPhoneNumber(user.phoneNo || ""),
      });
      setEmailNotificationsEnabled(Boolean(user.allowOperatorNotificationByEmail));
    }
  }, [user]);

  // Reset edit mode when modal opens
  useEffect(() => {
    if (isOpen) {
      setEditMode(false);
      setUnsavedChanges(false);
      setVerifiedPhoneNumber(null);
    }
  }, [isOpen]);

  // Update password validation effect
  useEffect(() => {
    const arePasswordsValid =
      passwordData.currentPassword.length > 0 &&
      passwordData.newPassword.length > 0 &&
      passwordData.confirmPassword.length > 0 &&
      passwordData.newPassword === passwordData.confirmPassword &&
      passwordData.newPassword.length >= 8;
    setIsButtonDisabled(!arePasswordsValid);

    // Update validation states
    setPasswordMatch(passwordData.newPassword === passwordData.confirmPassword && passwordData.confirmPassword.length > 0);
    setPasswordLength(passwordData.newPassword.length >= 8);
  }, [passwordData.currentPassword, passwordData.newPassword, passwordData.confirmPassword]);

  const toggleEditMode = () => {
    if (editMode && unsavedChanges) {
      setShowConfirmModal(true);
    } else {
      setEditMode(!editMode);
      // Reset fields if canceling edit
      if (editMode && user) {
        setEditableFields({
          name: user.name || "",
          email: user.email || "",
          phoneNo: formatPhoneNumber(user.phoneNo || ""),
        });
        setUnsavedChanges(false);
        setErrorMessage(null);
      }
    }
  };

  const handleFieldChange = (field: string, value: string) => {
    setEditableFields((prev) => ({
      ...prev,
      [field]: value,
    }));
    setUnsavedChanges(true);
  };

  // Helper function to format phone number as XXX-XXX-XXXX
  function formatPhoneNumber(value: string): string {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');

    // Apply formatting and limit to 10 digits
    let formatted = '';
    if (numbers.length > 0) {
      formatted = numbers.substring(0, 3);
    }
    if (numbers.length > 3) {
      formatted += '-' + numbers.substring(3, 6);
    }
    if (numbers.length > 6) {
      formatted += '-' + numbers.substring(6, 10);
    }

    return formatted;
  }

  // Normalize phone number to digits only for comparisons and API calls
  function normalizePhoneNumber(value: string): string {
    return value.replace(/\D/g, '');
  }

  // Helper function to validate email format
  const isValidEmail = (email: string): boolean => {
    // Basic regex for email validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Helper function to validate phone number format (XXX-XXX-XXXX)
  const isValidPhone = (phone: string): boolean => {
    // Phone must be non-empty and match the XXX-XXX-XXXX format with 10 digits
    if (!phone) return false; // Phone number is required
    const numbers = phone.replace(/\D/g, '');
    return numbers.length === 10;
  };

  // Validate fields and determine if Save button should be disabled
  const isSaveDisabled = !unsavedChanges || // Disable if no unsaved changes
    editableFields.name.trim() === '' || // Disable if name is empty
    !isValidEmail(editableFields.email) || // Disable if email is invalid or empty
    !isValidPhone(editableFields.phoneNo); // Disable if phone is invalid or empty

  const handleSaveAndClose = async () => {
    if (!user) return;

    // Check if phone number changed
    const newPhone = normalizePhoneNumber(editableFields.phoneNo);
    const phoneChanged = newPhone !== normalizePhoneNumber(user.phoneNo || '');

    // Only require verification if phone changed AND we haven't already verified this number
    if (phoneChanged && editableFields.phoneNo && newPhone !== verifiedPhoneNumber) {
      // Phone changed - require verification first
      setPendingPhoneUpdate(editableFields.phoneNo);
      setShowPhoneVerification(true);
      return;
    }

    // No phone change or already verified, proceed with normal save
    const didSave = await performSave();
    if (didSave) {
      onClose();
    }
  };

  // Called when phone verification is successful
  const handlePhoneVerified = async () => {
    setShowPhoneVerification(false);
    setPendingPhoneUpdate(null);

    // Perform the save - this will update the user data including the verified phone
    // We attempt to save, but regardless of outcome we close both modals
    // since the phone verification itself already updated the phone on the backend
    await performSave();

    // Always close both modals after successful phone verification
    // The phone is already verified/saved via OTP, so we're done
    setEditMode(false);
    setUnsavedChanges(false);
    setVerifiedPhoneNumber(null);
    onClose();
  };

  // Called when user cancels phone verification
  const handlePhoneVerificationCancel = () => {
    setShowPhoneVerification(false);
    setPendingPhoneUpdate(null);
    // Restore original phone number
    if (user) {
      setEditableFields(prev => ({ ...prev, phoneNo: formatPhoneNumber(user.phoneNo || '') }));
    }
    setUnsavedChanges(
      editableFields.name !== (user?.name || '') ||
      editableFields.email.toLowerCase() !== (user?.email || '').toLowerCase()
    );
  };

  // Perform the actual save operation
  const performSave = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      // Prepare update data with new schema
      // Omit role so the backend preserves the user's existing role.
      // Sending role from client state risks overwriting it if state is stale or uninitialized.
      const updateData = {
        name: editableFields.name,
        email: editableFields.email.toLowerCase(),
        phoneNo: normalizePhoneNumber(editableFields.phoneNo),
        modifiedBy: user.userId, // Current user's ID
        deleted: false // Always false for updates
      };

      // Only make the API call if there are actual changes
      if (editableFields.name !== user.name ||
        editableFields.email.toLowerCase() !== user.email.toLowerCase() ||
        normalizePhoneNumber(editableFields.phoneNo) !== normalizePhoneNumber(user.phoneNo || '')) {
        const response = await api.put(`/users/update-user-details/${user.userId}`, updateData);

        if (response.status === 200) {
          // Refresh user data
          await fetchUserData();
          setUnsavedChanges(false);
          setEditMode(false);
          setErrorMessage(null);
        }
      } else {
        // Even if no changes were made, ensure we exit edit mode
        setEditMode(false);
        setUnsavedChanges(false);
      }

      return true;
    } catch (error: any) {
      console.error("Error updating user:", error);
      const status = error.response?.status;
      const rawMessage = error.response?.data?.message || error.response?.data?.error || error.response?.data;
      let friendlyMessage = typeof rawMessage === 'string' ? rawMessage : "Failed to update account information";

      // Map known backend codes to user-friendly text
      if (status === 400 && typeof rawMessage === 'string') {
        if (/user00006/i.test(rawMessage) || /phone.*(taken|exists|already in use)/i.test(rawMessage)) {
          friendlyMessage = "Phone number already in use";
        }
      }

      setErrorMessage(friendlyMessage || "Failed to update account information");
      return false;
    }
  };

  const handleCancel = () => {
    if (user) {
      setEditableFields({
        name: user.name || "",
        email: user.email || "",
        phoneNo: formatPhoneNumber(user.phoneNo || ""),
      });
    }
    setUnsavedChanges(false);
    setEditMode(false);
    setErrorMessage(null);
    setVerifiedPhoneNumber(null);
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleToggleEmailNotifications = async () => {
    if (!user || isUpdatingNotifications) return;
    const previousValue = emailNotificationsEnabled;
    const nextValue = !emailNotificationsEnabled;
    setEmailNotificationsEnabled(nextValue);
    setIsUpdatingNotifications(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const response = await api.put("/users/operator-email-notifications", {
        allowOperatorNotificationByEmail: nextValue,
      });

      const updatedValue = response?.data?.data ?? nextValue;
      setEmailNotificationsEnabled(Boolean(updatedValue));
      await fetchUserData({ silent: true });
      setSuccessMessage(
        Boolean(updatedValue)
          ? "Email notifications enabled"
          : "Email notifications disabled"
      );
    } catch (error: any) {
      console.error("Failed to update email notification preference", error);
      setEmailNotificationsEnabled(previousValue);
      const rawMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      setErrorMessage(
        rawMessage || "Unable to update email notification preference"
      );
    } finally {
      setIsUpdatingNotifications(false);
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleChangePassword = async () => {
    if (!user) return;

    try {
      const response = await api.post(`/users/change-password/${user.userId}`, {
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      }, {
        validateStatus: (status) => status < 500
      });

      // Handle successful password change
      if (response.status === 200 && response.data?.success !== false) {
        // Show success message
        setSuccessMessage("Password changed successfully!");
        setErrorMessage(null);

        // Reset password fields and close modal after a short delay
        setTimeout(() => {
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setShowPasswordModal(false);
          setSuccessMessage(null);
          setPasswordMatch(false);
          setPasswordLength(false);
        }, 2000);
      } else {
        // Handle specific error cases from the response
        if (response.status === 401) {
          setErrorMessage("Incorrect current password");
        } else if (response.status === 403) {
          setErrorMessage("Password change not allowed");
        } else if (response.data?.message) {
          setErrorMessage(response.data.message);
        } else {
          setErrorMessage("Failed to change password. Please try again.");
        }

        // Clear the current password field on error
        setPasswordData(prev => ({
          ...prev,
          currentPassword: ""
        }));
      }
    } catch (error: any) {
      console.error("Error changing password:", error);
      setErrorMessage("An unexpected error occurred. Please try again.");

      // Clear the current password field on error
      setPasswordData(prev => ({
        ...prev,
        currentPassword: ""
      }));
    }
  };

  // Helper function to format the user ID by removing the "PWP-U-" prefix
  const formatUserId = (id: string): string => {
    const prefix = "PWP-U-";
    if (id && id.startsWith(prefix)) {
      return id.substring(prefix.length);
    }
    return id || "";
  };

  // Helper function to format displayUserId in the format "0000-0001"
  const formatDisplayId = (displayId: string): string => {
    if (!displayId) return '';
    return displayId.startsWith("U-") ? displayId.slice(2) : displayId;
  };

  const toggleOldPasswordVisibility = () => {
    setShowOldPassword(!showOldPassword);
  };

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const copyToClipboard = useCallback((value?: string) => {
    if (!value) return;
    const fallbackCopy = () => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      } catch (error) {
        console.error("Failed to copy text", error);
      }
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(value).catch(fallbackCopy);
    } else {
      fallbackCopy();
    }
  }, []);

  const handleCopyUserId = () => {
    if (!user?.displayUserId) return;
    copyToClipboard(user.displayUserId);
    setHasCopiedUserId(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setHasCopiedUserId(false), 500);
  };

  if (!user) {
    return (
      <Modal
        isOpen={isOpen}
        title="Account Information"
        confirmText="Close"
        cancelText="Cancel"
        onConfirm={onClose}
        onCancel={onClose}
      >
        <div className="account-modal-loading">Loading user information...</div>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Account Information"
        confirmText={editMode ? "Save and Close" : "Edit"}
        cancelText={editMode ? "Cancel" : "Close"}
        onConfirm={editMode ? handleSaveAndClose : handleEdit}
        onCancel={editMode ? handleCancel : onClose}
        disableConfirm={editMode && isSaveDisabled}
      >
        <div className="account-modal-content">
          {errorMessage && (
            <p className="error">{errorMessage}</p>
          )}

          <div className="account-details">
            <div className="account-row">
              <p className="account-label">User ID:</p>
              <div className="account-value">
                <div className="account-user-id-row">
                  <p className="account-disabled-text account-user-id">
                    {formatDisplayId(user.displayUserId || '')}
                  </p>
                  <button
                    type="button"
                    className="copy-button account-copy-button"
                    onClick={handleCopyUserId}
                    aria-label={hasCopiedUserId ? "User ID copied" : "Copy User ID"}
                    title={hasCopiedUserId ? "Copied!" : "Copy User ID"}
                  >
                    <img
                      src={hasCopiedUserId ? "/assets/copy-check.svg" : "/assets/copy.svg"}
                      alt={hasCopiedUserId ? "Copied" : "Copy"}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="account-row">
              <p className="account-label">Name:</p>
              <div className="account-value">
                {editMode ? (
                  <input
                    type="text"
                    className={`account-editable-highlight ${editableFields.name.trim() === '' ? 'invalid' : ''}`}
                    value={editableFields.name}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                  />
                ) : (
                  <p className="account-disabled-text account-field">
                    {editableFields.name}
                  </p>
                )}
              </div>
            </div>

            <div className="account-row">
              <p className="account-label">Email:</p>
              <div className="account-value">
                {editMode ? (
                  <input
                    type="email"
                    className={`account-editable-highlight ${!isValidEmail(editableFields.email) ? 'invalid' : ''}`}
                    value={editableFields.email}
                    onChange={(e) => handleFieldChange("email", e.target.value)}
                  />
                ) : (
                  <p className="account-disabled-text account-field">
                    {editableFields.email}
                  </p>
                )}
              </div>
            </div>

            <div className="account-row">
              <p className="account-label">Notifications:</p>
              <div className="account-value" onClick={(e) => e.stopPropagation()}>
                <div className="account-notification-row">
                  <Slider
                    checked={emailNotificationsEnabled}
                    onChange={handleToggleEmailNotifications}
                    disabled={isUpdatingNotifications}
                  />
                  <Tooltip
                    position="right"
                    content={
                      <div style={{ maxWidth: 280, whiteSpace: "normal", lineHeight: 1.4 }}>
                        Notifications for all lots you're added to will be sent to you via email. Disabling this setting does not disable
                        security or pricing related emails.
                      </div>
                    }
                  />
                </div>
              </div>
            </div>

            <div className="account-row">
              <p className="account-label">Phone:</p>
              <div className="account-value">
                {editMode ? (
                  <input
                    type="tel"
                    className={`account-editable-highlight ${!isValidPhone(editableFields.phoneNo) ? 'invalid' : ''}`}
                    value={editableFields.phoneNo}
                    onChange={(e) => {
                      const input = e.target;
                      const { value, selectionStart } = input;
                      const nativeEvent = e.nativeEvent as InputEvent;

                      // Remove all non-digits from the input value
                      const rawValue = value.replace(/\D/g, '');

                      // Handle backspace specifically to remove dash before the cursor
                      if (nativeEvent.inputType === 'deleteContentBackward' && selectionStart !== null) {
                        // Check if the character before the cursor in the original value was a dash
                        const charBeforeCursor = value[selectionStart - 1];
                        if (charBeforeCursor === '-') {
                          // If a dash was just before the cursor, remove the dash and the digit after it
                          const digitsBeforeDash = value.slice(0, selectionStart - 1).replace(/\D/g, '');
                          const digitsAfterDash = value.slice(selectionStart).replace(/\D/g, '');
                          const combinedValue = digitsBeforeDash + digitsAfterDash.slice(1); // drop the first digit after the dash
                          const formatted = formatPhoneNumber(combinedValue);
                          handleFieldChange("phoneNo", formatted);

                          // Place cursor where the dash sat
                          const newCursorPosition = Math.max(0, selectionStart - 1);
                          setTimeout(() => {
                            input.selectionStart = input.selectionEnd = newCursorPosition;
                          }, 0);
                          return; // Stop further processing
                        }
                      }

                      // Limit to 10 digits before formatting
                      const limitedValue = rawValue.slice(0, 10);

                      // Format the digits
                      const formattedValue = formatPhoneNumber(limitedValue);

                      // Calculate potential cursor position after formatting
                      // This is a more robust approach that accounts for added/removed dashes
                      const oldDashCount = (value.slice(0, selectionStart || 0).match(/-/g) || []).length;
                      const newDashCount = (formattedValue.slice(0, selectionStart || 0 + (formattedValue.length - value.length)).match(/-/g) || []).length;
                      const cursorAdjustment = newDashCount - oldDashCount;
                      const newCursorPosition = (selectionStart || 0) + cursorAdjustment;

                      // Update state with formatted value
                      handleFieldChange("phoneNo", formattedValue);

                      // Set the cursor position
                      setTimeout(() => {
                        input.selectionStart = input.selectionEnd = Math.max(0, Math.min(newCursorPosition, formattedValue.length));
                      }, 0);
                    }}
                    maxLength={12} // XXX-XXX-XXXX format is 12 characters long
                  />
                ) : (
                  <p className="account-disabled-text account-field">
                    {editableFields.phoneNo || "Not provided"}
                  </p>
                )}
              </div>
            </div>

            <div className="account-row">
              <p className="account-label">Password:</p>
              <div className="account-value">
                <p className="account-password">
                  ••••••••
                  <button
                    className="account-reset-password-btn"
                    onClick={() => setShowPasswordModal(true)}
                    disabled={!editMode}
                  >
                    Change Password
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <Modal
          isOpen={showConfirmModal}
          title="Confirm Changes"
          description="You have unsaved changes. Would you like to save them?"
          confirmText="Save Changes"
          cancelText="Discard Changes"
          onConfirm={handleSaveAndClose}
          onCancel={handleCancel}
        />
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <Modal
          isOpen={showPasswordModal}
          title="Change Password"
          confirmText="Change Password"
          cancelText="Cancel"
          onConfirm={() => handleChangePassword()}
          onCancel={() => {
            setShowPasswordModal(false);
            setPasswordData({
              currentPassword: "",
              newPassword: "",
              confirmPassword: "",
            });
            setErrorMessage(null);
            setSuccessMessage(null);
            setPasswordMatch(false);
            setPasswordLength(false);
          }}
          disableConfirm={isButtonDisabled}
        >
          <form
            className="account-password-form"
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              handleChangePassword();
            }}
            method="post"
            autoComplete="new-password"
          >
            {errorMessage && (
              <p className="error">{errorMessage}</p>
            )}
            {successMessage && (
              <p className="success" style={{ color: 'var(--success)', marginBottom: '16px' }}>{successMessage}</p>
            )}

            <div className="form-group" style={{ position: 'relative', marginBottom: '16px' }}>
              <input
                id="currentPassword"
                type={showOldPassword ? 'text' : 'password'}
                placeholder="Current Password"
                value={passwordData.currentPassword}
                onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
                autoComplete="current-password"
                required
                style={{ paddingRight: '40px' }}
              />
              {passwordData.currentPassword && (
                <button
                  type="button"
                  onClick={toggleOldPasswordVisibility}
                  className="password-toggle-button"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer'
                  }}
                >
                  <img
                    src={showOldPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                    alt={showOldPassword ? "Hide password" : "Show password"}
                    width="24"
                    height="24"
                  />
                </button>
              )}
            </div>

            <div className="form-group" style={{ position: 'relative', marginBottom: '16px' }}>
              <input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                placeholder="New Password"
                value={passwordData.newPassword}
                onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
                autoComplete="new-password"
                required
                style={{ paddingRight: '40px' }}
              />
              {passwordData.newPassword && (
                <button
                  type="button"
                  onClick={toggleNewPasswordVisibility}
                  className="password-toggle-button"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer'
                  }}
                >
                  <img
                    src={showNewPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                    alt={showNewPassword ? "Hide password" : "Show password"}
                    width="24"
                    height="24"
                  />
                </button>
              )}
            </div>

            <div className="form-group" style={{ position: 'relative', marginBottom: '16px' }}>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm New Password"
                value={passwordData.confirmPassword}
                onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
                autoComplete="new-password"
                required
                style={{ paddingRight: '40px' }}
              />
              {passwordData.confirmPassword && (
                <button
                  type="button"
                  onClick={toggleConfirmPasswordVisibility}
                  className="password-toggle-button"
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '0',
                    cursor: 'pointer'
                  }}
                >
                  <img
                    src={showConfirmPassword ? "/assets/HidePassword.svg" : "/assets/ShowPassword.svg"}
                    alt={showConfirmPassword ? "Hide password" : "Show password"}
                    width="24"
                    height="24"
                  />
                </button>
              )}
            </div>

            {/* Password validation feedback */}
            <div style={{ marginTop: '10px', fontSize: '14px' }}>
              {passwordData.newPassword && (
                <p style={{
                  color: passwordLength ? 'var(--success)' : 'var(--error)',
                  margin: '5px 0'
                }}>
                  {passwordLength ? '✓ Password is at least 8 characters' : '✗ Password must be at least 8 characters'}
                </p>
              )}
              {passwordData.confirmPassword && (
                <p style={{
                  color: passwordMatch ? 'var(--success)' : 'var(--error)',
                  margin: '5px 0'
                }}>
                  {passwordMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
                </p>
              )}
            </div>
          </form>
        </Modal>
      )}

      {/* Phone Verification Modal */}
      {showPhoneVerification && pendingPhoneUpdate && (
        <PhoneVerificationModal
          isOpen={showPhoneVerification}
          phoneNumber={pendingPhoneUpdate}
          onVerified={handlePhoneVerified}
          onCancel={handlePhoneVerificationCancel}
        />
      )}
    </>
  );
};

export default AccountModal; 
